// src/components/IssueReceiveTable.jsx
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from 'react-redux';
import { tokens } from "../../theme";
import axios from 'axios';
import { Box } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Button, TextField, MenuItem, Select, InputLabel,IconButton, FormControl } from '@mui/material';
import { useMediaQuery, useTheme, InputAdornment } from "@mui/material";
import { Add, Edit, Delete, Print, Search, Refresh } from '@mui/icons-material';
import { format } from 'date-fns';
import env from "react-dotenv";
import ExcelJS from 'exceljs';
 import { saveAs } from 'file-saver';
 import SaveAltIcon from '@mui/icons-material/SaveAlt';
 import FilterListIcon from '@mui/icons-material/FilterList';
 import ClearIcon from '@mui/icons-material/Clear';
 import CustomSelect from '../../utils/CustomSelect'; // Import the CustomSelect component

const IssueReceiveTable = () => {
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
   const [endDate, setEndDate] = useState('');
   const [selectedMaterials, setSelectedMaterials] = useState([]);
   const [selectedUsers, setSelectedUsers] = useState([]);
   const [selectedStatus, setSelectedStatus] = useState('');
   const [columnFilters, setColumnFilters] = useState({});
   const [showAdvancedFilters, setShowAdvancedFilters] = useState(false); // New state for advanced filters
   const [rawMaterials, setRawMaterials] = useState([]); // State for raw materials
   const [selectedGrnNo, setSelectedGrnNo] = useState(''); // New state for GRN No.

   // States to control dropdown opens separately
     const [openMaterialsDropdown, setOpenMaterialsDropdown] = useState(false);
     const [openUserDropdown, setOpenUserDropdown] = useState(false);

  const user = useSelector((state) => state.user);
  // const isAdmin = user?.role === 'Store Admin'; // Determine if the user is an Admin
  const isAdmin = (user?.role === 'Admin' && user?.department?.includes('Store')) || 
                  (user?.role === 'Super Admin' && user?.department?.includes('Store'));

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!location.key || location.key === 'default') {
      navigate("/");
    }
  }, [location, navigate]);

  // Fetch records on component mount
  useEffect(() => {
    const fetchRecords = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_PJCBACKEND_URL}/api/products/issue-receive-items`
        );
        setRecords(response.data);
        setFilteredRecords(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching records:', error);
        setLoading(false);
      }
    };

    fetchRecords();
  }, []);

  // Add computed property for display
  const processedRecords = filteredRecords.map(record => {
    let displayLocation = record.location || ''; // Assuming 'location' is a field in your records
    if (record.plan_id && record.job_card_id) {
      displayLocation += `/${record.plan_id}/${record.job_card_id}`;
    } else if (record.slipNo) {
      displayLocation += `/${record.slipNo}`;
    }
    return {
      ...record,
      displayLocation // Add the new property to the record
    };
  });

  useEffect(() => {
    const fetchRawMaterials = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_PJCBACKEND_URL}/api/products/list`
        );
        setRawMaterials(response.data); // Assuming the response is an array of raw materials
      } catch (error) {
        console.error('Error fetching raw materials:', error);
      }
    };
  
    fetchRawMaterials();
  }, []);

  const SearchByAnyField = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);

    const filtered = records.filter((record) =>
      Object.values(record).some((field) =>
        field && field.toString().toLowerCase().includes(value)
      )
    );

    setFilteredRecords(filtered);
  };
  useEffect(() => {
    const filtered = records.filter(record => {
      const matchesSearch = Object.values(record).some(field => field && field.toString().toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesMaterial = selectedMaterials.length === 0 || selectedMaterials.includes(record.product_name);
      const matchesUser = selectedUsers.length === 0 || selectedUsers.includes(record.user_name);
      const matchesStatus = !selectedStatus || record.item_status === selectedStatus;
      const matchesGrnNo = !selectedGrnNo || record.grn_no === selectedGrnNo; // New filter for GRN No.
  
      let matchesDate = true;
      if (startDate) {
        const itemDate = new Date(record.item_date);
        if (endDate) {
          matchesDate = itemDate >= new Date(startDate) && itemDate <= new Date(endDate);
        } else {
          matchesDate = itemDate.toDateString() === new Date(startDate).toDateString();
        }
      }
  
      const columnMatches = Object.entries(columnFilters).every(([column, filterValue]) => {
        if (filterValue) {
          return String(record[column]).toLowerCase().includes(filterValue.toLowerCase());
        }
        return true;
      });
  
      return matchesSearch && matchesMaterial && matchesUser && matchesStatus && matchesDate && matchesGrnNo && columnMatches;
    });
  
    setFilteredRecords(filtered);
  }, [searchTerm, selectedMaterials, selectedUsers, selectedStatus, selectedGrnNo, startDate, endDate, columnFilters, records]);

  const handleExport = async () => {
    // Use processedRecords instead of records
    const filtered = processedRecords.filter(record => {
        const itemDate = new Date(record.item_date);
        const start = new Date(startDate);
        const end = new Date(endDate);
        const isDateInRange = (!startDate || (endDate ? (itemDate >= start && itemDate <= end) : itemDate.toDateString() === start.toDateString()));
        const isMaterialSelected = selectedMaterials.length === 0 || selectedMaterials.includes(record.product_name);
        const isUserSelected = selectedUsers.length === 0 || selectedUsers.includes(record.user_name);
        const isStatusSelected = !selectedStatus || record.item_status === selectedStatus;
        const isGrnNoSelected = !selectedGrnNo || record.grn_no === selectedGrnNo; // Include GRN No. filter

        return isDateInRange && isMaterialSelected && isUserSelected && isStatusSelected && isGrnNoSelected;
    });

    const excelHeaders = [
        'S/N',
        'Creation Date',
        'Raw Material Name',
        'Before Qty',
        'Issue/Receive Qty',
        'After Qty',
        'Status',
        'User  ',
        'Vendor Name', // New header
        'GRN No.',    // New header
        'GRN Date',   // New header
        'Location',    // New header for displayLocation
        'Remark'       // New header for comments
    ];

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('IssueReceive');

    // Add header rows
    const titleRow = worksheet.addRow(['ARYAN EXPORTERS PVT. LTD.']);
    worksheet.mergeCells(`A1:M1`); // Merge cells A1 to M1
    titleRow.alignment = { horizontal: 'center', vertical: 'middle' }; // Center alignment

    // Set font for the merged cell
    const mergedTitleCell = worksheet.getCell('A1');
    mergedTitleCell.font = { bold: true, size: 14 }; // Set font for the merged cell

    const summaryRow = worksheet.addRow([`Summary Of Issue/Receive Item ${startDate && endDate ? `from ${startDate} to ${endDate}` : startDate ? `on ${startDate}` : 'for all dates'}`]);
    worksheet.mergeCells(`A2:M2`); // Merge cells A2 to M2
    summaryRow.alignment = { horizontal: 'center', vertical: 'middle' }; // Center alignment

    // Set font for the merged cell
    const mergedSummaryCell = worksheet.getCell('A2');
    mergedSummaryCell.font = { bold: true, size: 12 }; // Set font for the merged cell

    worksheet.addRow(excelHeaders).font = { bold: true };

    // Add data rows
    filtered.forEach((record, index) => {
        worksheet.addRow([
            index + 1,
            record.item_date || '', // Ensure empty values are handled
            record.product_name || '',
            record.initial_quantity || '',
            record.quantity || '',
            record.final_quantity || '',
            record.item_status || '',
            record.user_name || '',
            record.vendor_name || '', // New field
            record.grn_no || '',      // New field
            record.grn_date || '',     // New field
            record.displayLocation || '', // New field for displayLocation
            record.comments || ''         // New field for comments
        ]);
    });

    // Apply styles to the worksheet
    worksheet.eachRow((row, rowNumber) => {
        row.eachCell((cell) => {
            // Apply border to all cells, including empty ones
            cell.border = {
                top: { style: 'thin' },
                bottom: { style: 'thin' },
                left: { style: 'thin' },
                right: { style: 'thin' },
            };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
        });

        // Bold for header rows
        if (rowNumber <= 2) {
            row.font = { bold: true };
        }
    });

    // Set column widths
    worksheet.getColumn(1).width = 5;   // S/N
    worksheet.getColumn(2).width = 12;  // Creation Date
    worksheet.getColumn(3).width = 25;  // Raw Material Name
    worksheet.getColumn(4).width = 10;  // Before Qty
    worksheet.getColumn(5).width = 18;  // Issue/Receive Qty
    worksheet.getColumn(6).width = 10;  // After Qty
    worksheet.getColumn(7).width = 10;  // Status
    worksheet.getColumn(8).width = 15;  // User
    worksheet.getColumn(9).width = 40;  // Vendor Name
    worksheet.getColumn(10).width = 12; // GRN No.
    worksheet.getColumn(11).width = 12; // GRN Date
    worksheet.getColumn(12).width = 20; // Location
    worksheet.getColumn(13).width = 18; // Remark

    // Save the workbook
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    saveAs(blob, 'IssueReceiveData.xlsx');
};    

  //const uniqueMaterials = [...new Set(records.map(record => record.product_name))];
  const uniqueUsers = [...new Set(records.map(record => record.user_name))];
  const uniqueStatuses = [...new Set(records.map(record => record.item_status))];
  const uniqueGrnNos = [...new Set(records.map(record => record.grn_no))]; // New unique GRN No. extraction

  const clearFilters = () => {
    setSearchTerm("");
    setStartDate("");
    setEndDate("");
    setSelectedMaterials([]);
    setSelectedUsers([]);
    setSelectedStatus("");
    setSelectedGrnNo(""); // Reset GRN No. filter
  };


  const columns = [
    // { field: 'id', headerName: 'ID', width: 100 },
    // { field: 'product_id', headerName: 'Product ID', width: 150 },
    { field: 'item_date', headerName: 'Creation Date', width: 90 },
    { field: 'product_name', headerName: 'Raw Material Name', width: 130 },
    { field: 'initial_quantity', headerName: 'Before Qty', width: 90 },
    { field: 'quantity', headerName: 'Issue/Receive Qty', width: 120 },
    { field: 'final_quantity', headerName: 'EOD Qty', width: 90 },
    { field: 'item_status', headerName: 'Status', width: 80 },
    { field: 'user_name', headerName: 'User', width: 110 },
    { field: 'vendor_name', headerName: 'Vendor Name', width: 100 },
    { field: 'grn_no', headerName: 'GRN No.', width: 80 },
    { field: 'grn_date', headerName: 'GRN Date', width: 90 },
    // { field: 'job_card_id', headerName: 'JC ID', width: 80, headerAlign: 'center', align: 'center',}
    { field: 'displayLocation', headerName: 'Location', width: 180 }, // New column
    { field: 'comments', headerName: 'Remark', width: 90 } // New column
  ];

  const isNonMobileScreens = useMediaQuery("(min-width: 1000px)");

  return (
    <Box style={{  
      height: 500,
      width: '95%',
      marginLeft:"180px",
      paddingRight:"90px",
      marginTop:"0px"
      }}>
      
      {/* {isAdmin && ( */}
        <Box
          sx={{
            marginTop: "5px",
            marginBottom: "10px",
            display: "flex",
            gap: "10px",
            alignItems: "center",
          }}
        >
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{
              // marginLeft: "10px",
              backgroundColor: '#ffffff',
              width: '1200px', // Set the desired width here
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#00308F',
                },
                '&:hover fieldset': {
                  borderColor: '#00207E',
                },
              },
            }}

          />
          {/* Date Filters */}
          {/* Advanced Filter Button */}
         <Button
           onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
           startIcon={<FilterListIcon />} // Add the filter icon here
         >
           Filter
         </Button>
 
           <Button 
           onClick={handleExport}
           startIcon={<SaveAltIcon />}
           >
             Export
           </Button>
 
           <IconButton
           onClick={() => window.location.reload()} // Refresh the page
           color="primary"
           sx={{
             backgroundColor: '#ffffff',
             '&:hover': {
               backgroundColor: '#f0f0f0',
             },
           }}
         >
           <Refresh />
         </IconButton>
 
         </Box>
 
       {/* Advanced Filters */}
       {showAdvancedFilters && (
         <Box sx={{ marginTop: "15px", marginBottom: "10px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
           {/* Date Filters */}
          <TextField label="From" variant="outlined" size="small" type="date" value={startDate} 
           onChange={(e) => setStartDate(e.target.value)} sx={{ backgroundColor: '#ffffff' }} 
           InputLabelProps={{
             shrink: true, }} />
 
           <TextField label="To" variant="outlined" size="small" type="date" value={endDate} 
           onChange={(e) => setEndDate(e.target.value)} sx={{ backgroundColor: '#ffffff' }}
           InputLabelProps={{
             shrink: true, }} />
 
           {/* Material Filter */}
           {/* <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
             <InputLabel>Raw Material</InputLabel>
             <Select
               multiple
               value={selectedMaterials}
               onChange={(e) => setSelectedMaterials(e.target.value)}
               label="Raw Material"
               renderValue={(selected) => selected.join(', ')}
             >
                 {rawMaterials.map((material) => (
                 <MenuItem key={material.product_name} value={material.product_name}>
                   {material.product_name}
                 </MenuItem>
               ))}
             </Select>
           </FormControl> */}

                            <CustomSelect
                               label="Raw Material"
                               value={selectedMaterials}
                               onChange={(e) => setSelectedMaterials(e.target.value)}
                               options={rawMaterials}
                               open={openMaterialsDropdown}
                               setOpen={setOpenMaterialsDropdown}
                               getOptionLabel={(option) => option.product_name} // Extract product_name from each object
                             />
 
           {/* Status Filter */}
           <FormControl variant="outlined" size="small" sx={{ minWidth: 78 }}>
             <InputLabel>Status</InputLabel>
             <Select
               value={selectedStatus}
               onChange={(e) => setSelectedStatus(e.target.value)}
               label="Status"
             >
               {uniqueStatuses.map((status) => (
                 <MenuItem key={status} value={status}>
                   {status}
                 </MenuItem>
               ))}
             </Select>
           </FormControl>
 
           {/* User Filter */}
           {/* <FormControl variant="outlined" size="small" sx={{ minWidth: 68 }}>
             <InputLabel>User</InputLabel>
             <Select
               multiple
               value={selectedUsers}
               onChange={(e) => setSelectedUsers(e.target.value)}
               label="User"
               renderValue={(selected) => selected.join(', ')}
             >
               {uniqueUsers.map((user) => (
                 <MenuItem key={user} value={user}>
                   {user}
                 </MenuItem>
               ))}
             </Select>
           </FormControl> */}

           <CustomSelect
            label="User"
            value={selectedUsers}
            onChange={(e) => setSelectedUsers(e.target.value)}
            options={uniqueUsers}
            open={openUserDropdown}
            setOpen={setOpenUserDropdown}
            getOptionLabel={(option) => option} // If uniqueUsers is an array of strings
          />

          {/* New GRN No. Filter */}
          <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
            <InputLabel>GRN No.</InputLabel>
            <Select
              value={selectedGrnNo}
              onChange={(e) => setSelectedGrnNo(e.target.value)}
              label="GRN No."
            >
              {uniqueGrnNos.map((grnNo) => (
                <MenuItem key={grnNo} value={grnNo}>
                  {grnNo}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
 
            {/* Clear All Filters Button */}
              <Button
                onClick={clearFilters}
                // startIcon={<ClearIcon />} // Add the clear icon here
              >
                Clear Filter
              </Button>
        </Box>      
      )}

      <Box
        sx={{
          height: 500,
          overflowY: 'auto',
        }}
      >
        <DataGrid
          // rows={filteredRecords}
          rows={processedRecords} // Use processed records
          columns={columns}
          getRowId={(row) => row.id}
          pageSizeOptions={[10, 25, 50]}
          checkboxSelection
          disableRowSelectionOnClick
          // slots={{ toolbar: GridToolbar }}
          sx={{
            boxShadow: 1,
            border: 1,
            borderColor: 'primary.light',
            typography: 'subtitle2',
            '& .MuiDataGrid-cell:hover': {
              color: 'primary.main',
            },
            '.MuiDataGrid-columnHeaderTitle': {
              font: 'light important',
              overflow: 'visible !important',
            },
            '--DataGrid-overlayHeight': '300px'
          }}
        />
      </Box>
    </Box>
  );
};

export default IssueReceiveTable;
